import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { ThemeProvider, getStoredTheme } from "./theme/ThemeProvider";
import { useAuthStore } from "./store/authStore";
import { LoginPage } from "./pages/auth/LoginPage";
import { SignupPage } from "./pages/auth/SignupPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { VerifyEmailPage } from "./pages/auth/VerifyEmailPage";
import { VerifyPhonePage } from "./pages/auth/VerifyPhonePage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { ForcePasswordChangePage } from "./pages/auth/ForcePasswordChangePage";
import { AppShell } from "./components/layout/AppShell";
import { RequireOrgRole } from "./components/guards/RequireOrgRole";
import { RequirePermissionRoute } from "./components/guards/RequirePermissionRoute";
import { RequireFeatureRoute } from "./components/guards/RequireFeatureRoute";
import { RequireAnyPermissionRoute } from "./components/guards/RequireAnyPermissionRoute";
import { ACCOUNTING_FEATURE } from "./lib/featureFlags";
import { LedgerUIBridge } from "./lib/ledgerUiBridge";
import { Spinner } from "./components/ui/Spinner";

// Lazy load pages
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage").then(m => ({ default: m.DashboardPage })));
const TwoStepPage = lazy(() => import("./pages/account/TwoStepPage").then(m => ({ default: m.TwoStepPage })));

// Org pages
const OrgSettingsPage = lazy(() => import("./pages/org/OrgSettingsPage").then(m => ({ default: m.OrgSettingsPage })));
const MembersPage = lazy(() => import("./pages/org/MembersPage").then(m => ({ default: m.MembersPage })));
const RoleTemplatesPage = lazy(() => import("./pages/org/RoleTemplatesPage").then(m => ({ default: m.RoleTemplatesPage })));
const BusinessesPage = lazy(() => import("./pages/org/BusinessesPage").then(m => ({ default: m.BusinessesPage })));
const SubscriptionPage = lazy(() => import("./pages/org/SubscriptionPage").then(m => ({ default: m.SubscriptionPage })));

// Business pages
const BusinessSettingsPage = lazy(() => import("./pages/business/BusinessSettingsPage").then(m => ({ default: m.BusinessSettingsPage })));
const OutletsPage = lazy(() => import("./pages/business/OutletsPage").then(m => ({ default: m.OutletsPage })));
const PrintSettingsPage = lazy(() => import("./pages/business/PrintSettingsPage").then(m => ({ default: m.PrintSettingsPage })));
const OutletFormPage = lazy(() => import("./pages/business/OutletFormPage").then(m => ({ default: m.OutletFormPage })));
const StaffPage = lazy(() => import("./pages/business/StaffPage").then(m => ({ default: m.StaffPage })));
const RolesPage = lazy(() => import("./pages/business/RolesPage").then(m => ({ default: m.RolesPage })));

// Menu pages
const CategoriesPage = lazy(() => import("./pages/menu/CategoriesPage").then(m => ({ default: m.CategoriesPage })));
const ItemsPage = lazy(() => import("./pages/menu/ItemsPage").then(m => ({ default: m.ItemsPage })));
const ItemFormPage = lazy(() => import("./pages/menu/ItemFormPage").then(m => ({ default: m.ItemFormPage })));
const ImportPage = lazy(() => import("./pages/menu/ImportPage").then(m => ({ default: m.ImportPage })));

// Inventory pages
const InventoryPage = lazy(() => import("./pages/inventory/InventoryPage").then(m => ({ default: m.InventoryPage })));
const AdjustStockPage = lazy(() => import("./pages/inventory/AdjustStockPage").then(m => ({ default: m.AdjustStockPage })));
const TransactionsPage = lazy(() => import("./pages/inventory/TransactionsPage").then(m => ({ default: m.TransactionsPage })));
const BulkUpdatePage = lazy(() => import("./pages/inventory/BulkUpdatePage").then(m => ({ default: m.BulkUpdatePage })));

// Notifications (the bell's "see all" — history and bulk clear)
const NotificationsPage = lazy(() => import("./pages/notifications/NotificationsPage").then(m => ({ default: m.NotificationsPage })));

// Supply chain pages
const SuppliersPage = lazy(() => import("./pages/supply-chain/SuppliersPage").then(m => ({ default: m.SuppliersPage })));
const PurchaseOrdersPage = lazy(() => import("./pages/supply-chain/PurchaseOrdersPage").then(m => ({ default: m.PurchaseOrdersPage })));
const PurchaseOrderFormPage = lazy(() => import("./pages/supply-chain/PurchaseOrderFormPage").then(m => ({ default: m.PurchaseOrderFormPage })));
const PODetailPage = lazy(() => import("./pages/supply-chain/PODetailPage").then(m => ({ default: m.PODetailPage })));
const POReceivePage = lazy(() => import("./pages/supply-chain/POReceivePage").then(m => ({ default: m.POReceivePage })));
const SupplierDetailPage = lazy(() => import("./pages/supply-chain/SupplierDetailPage").then(m => ({ default: m.SupplierDetailPage })));
const PayablesPage = lazy(() => import("./pages/supply-chain/PayablesPage").then(m => ({ default: m.PayablesPage })));

// Other pages
const CustomersPage = lazy(() => import("./pages/customers/CustomersPage").then(m => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import("./pages/customers/CustomerDetailPage").then(m => ({ default: m.CustomerDetailPage })));
const SalesPage = lazy(() => import("./pages/sales/SalesPage").then(m => ({ default: m.SalesPage })));
const SaleEntryPage = lazy(() => import("./pages/sales/SaleEntryPage").then(m => ({ default: m.SaleEntryPage })));
const BillsPage = lazy(() => import("./pages/bills/BillsPage").then(m => ({ default: m.BillsPage })));
const BillDetailPage = lazy(() => import("./pages/bills/BillDetailPage").then(m => ({ default: m.BillDetailPage })));
const CreditNotesPage = lazy(() => import("./pages/bills/CreditNotesPage").then(m => ({ default: m.CreditNotesPage })));
const SalesSummaryPage = lazy(() => import("./pages/reports/SalesSummaryPage").then(m => ({ default: m.SalesSummaryPage })));
const ItemSalesPage = lazy(() => import("./pages/reports/ItemSalesPage").then(m => ({ default: m.ItemSalesPage })));
const GSTReportPage = lazy(() => import("./pages/reports/GSTReportPage").then(m => ({ default: m.GSTReportPage })));
const CompositionReportPage = lazy(() => import("./pages/reports/CompositionReportPage").then(m => ({ default: m.CompositionReportPage })));
const DayClosePage = lazy(() => import("./pages/reports/DayClosePage").then(m => ({ default: m.DayClosePage })));
const PaymentReconciliationPage = lazy(() => import("./pages/reports/PaymentReconciliationPage").then(m => ({ default: m.PaymentReconciliationPage })));
const StaffPerformancePage = lazy(() => import("./pages/reports/StaffPerformancePage").then(m => ({ default: m.StaffPerformancePage })));
const StockFlowPage = lazy(() => import("./pages/reports/StockFlowPage").then(m => ({ default: m.StockFlowPage })));
// Receivables reports (#17)
const PriceBandsPage = lazy(() => import("./pages/menu/PriceBandsPage").then(m => ({ default: m.PriceBandsPage })));
const PricingExceptionsPage = lazy(() => import("./pages/reports/PricingExceptionsPage").then(m => ({ default: m.PricingExceptionsPage })));
const QuotationsPage = lazy(() => import("./pages/quotations/QuotationsPage").then(m => ({ default: m.QuotationsPage })));
const QuotationFormPage = lazy(() => import("./pages/quotations/QuotationFormPage").then(m => ({ default: m.QuotationFormPage })));
const QuotationDetailPage = lazy(() => import("./pages/quotations/QuotationDetailPage").then(m => ({ default: m.QuotationDetailPage })));
const BusinessDayPage = lazy(() => import("./pages/reports/BusinessDayPage").then(m => ({ default: m.BusinessDayPage })));
const MarginPage = lazy(() => import("./pages/reports/MarginPage").then(m => ({ default: m.MarginPage })));
const StockValuePage = lazy(() => import("./pages/reports/StockValuePage").then(m => ({ default: m.StockValuePage })));
const CostCheckPage = lazy(() => import("./pages/reports/CostCheckPage").then(m => ({ default: m.CostCheckPage })));
const ReceivablesAgeingPage = lazy(() => import("./pages/reports/ReceivablesAgeingPage").then(m => ({ default: m.ReceivablesAgeingPage })));
const SalesByCustomerPage = lazy(() => import("./pages/reports/SalesByCustomerPage").then(m => ({ default: m.SalesByCustomerPage })));
// Production pages (#9)
const ProductionPage = lazy(() => import("./pages/production/ProductionPage").then(m => ({ default: m.ProductionPage })));
const ProductionRunFormPage = lazy(() => import("./pages/production/ProductionRunFormPage").then(m => ({ default: m.ProductionRunFormPage })));
const ProductionRunDetailPage = lazy(() => import("./pages/production/ProductionRunDetailPage").then(m => ({ default: m.ProductionRunDetailPage })));
const DaySheetPage = lazy(() => import("./pages/production/DaySheetPage").then(m => ({ default: m.DaySheetPage })));
const MenuPerformancePage = lazy(() => import("./pages/reports/MenuPerformancePage").then(m => ({ default: m.MenuPerformancePage })));

// Accounting and Financial Statements
// Four of the five accounting pages live in the reusable @tanvishtech/ledger-ui
// package (extraction plan, Phase 5); PettyCashPage is host-owned and stays.
// App specifics reach the package through LedgerUIBridge on the route group.
const OpeningBalancesPage = lazy(() => import("@tanvishtech/ledger-ui").then(m => ({ default: m.OpeningBalancesPage })));
const AccountsPage = lazy(() => import("@tanvishtech/ledger-ui").then(m => ({ default: m.AccountsPage })));
const JournalsPage = lazy(() => import("@tanvishtech/ledger-ui").then(m => ({ default: m.JournalsPage })));
const PettyCashPage = lazy(() => import("./pages/accounting/PettyCashPage").then(m => ({ default: m.PettyCashPage })));
const CashBookPage = lazy(() => import("@tanvishtech/ledger-ui").then(m => ({ default: m.CashBookPage })));
const FinancialReportsPage = lazy(() => import("./pages/reports/FinancialReportsPage").then(m => ({ default: m.FinancialReportsPage })));

// ── Protected route ───────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Show loading while initializing or during auth check
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-alt">
        <Spinner size="lg" className="text-brand" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// ── Admin suspense fallback ───────────────────────────────────────────────────

function AdminFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" className="text-brand" />
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

function AppInner() {
  const initialize = useAuthStore((s) => s.initialize);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const storedTheme = getStoredTheme();

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [initialize, isInitialized]);

  return (
    <ThemeProvider themeName={storedTheme}>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-phone" element={<VerifyPhonePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ForcePasswordChangePage />} />

          {/* Root redirect */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />

          {/* Authenticated routes with AppShell */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            {/* The signed-in user's own two-step sign-in (#159) */}
            <Route
              path="account/two-step"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <TwoStepPage />
                </Suspense>
              }
            />

            {/* Organization routes (owner/admin only) — cloud mode only */}
            <Route
              path="org"
              element={
                <RequireOrgRole>
                  <Outlet />
                </RequireOrgRole>
              }
            >
              <Route index element={<Navigate to="settings" replace />} />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <OrgSettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="members"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <MembersPage />
                  </Suspense>
                }
              />
              <Route
                path="role-templates"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <RoleTemplatesPage />
                  </Suspense>
                }
              />
              <Route
                path="businesses"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <BusinessesPage />
                  </Suspense>
                }
              />
              <Route
                path="subscription"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <SubscriptionPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Business routes */}
            <Route path="business">
              <Route index element={<Navigate to="settings" replace />} />
              <Route
                path="settings"
                element={
                  <RequirePermissionRoute permission="settings:view">
                    <Suspense fallback={<AdminFallback />}>
                      <BusinessSettingsPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="outlets"
                element={
                  <RequirePermissionRoute permission="outlets:view">
                    <Suspense fallback={<AdminFallback />}>
                      <OutletsPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="outlets/new"
                element={
                  <RequirePermissionRoute permission="outlets:view">
                    <Suspense fallback={<AdminFallback />}>
                      <OutletFormPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="outlets/:id"
                element={
                  <RequirePermissionRoute permission="outlets:view">
                    <Suspense fallback={<AdminFallback />}>
                      <OutletFormPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="outlets/:id/print-settings"
                element={
                  <RequirePermissionRoute permission="outlets:manage">
                    <Suspense fallback={<AdminFallback />}>
                      <PrintSettingsPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="staff"
                element={
                  <RequirePermissionRoute permission="staff:view">
                    <Suspense fallback={<AdminFallback />}>
                      <StaffPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="roles"
                element={
                  <RequirePermissionRoute permission="roles:view">
                    <Suspense fallback={<AdminFallback />}>
                      <RolesPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
            </Route>

            {/* Menu routes */}
            <Route path="menu">
              <Route
                path="categories"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <CategoriesPage />
                  </Suspense>
                }
              />
              <Route
                path="items"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <ItemsPage />
                  </Suspense>
                }
              />
              <Route
                path="items/new"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <ItemFormPage />
                  </Suspense>
                }
              />
              <Route
                path="items/:id"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <ItemFormPage />
                  </Suspense>
                }
              />
              <Route
                path="import"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <ImportPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Inventory routes */}
            <Route path="inventory">
              <Route
                path=""
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <InventoryPage />
                  </Suspense>
                }
              />
              <Route
                path="adjust"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdjustStockPage />
                  </Suspense>
                }
              />
              <Route
                path="bulk"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <BulkUpdatePage />
                  </Suspense>
                }
              />
              <Route
                path="transactions"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <TransactionsPage />
                  </Suspense>
                }
              />
            </Route>

            {/* One customer: what they owe, their terms, their bills (#17) */}
            <Route
              path="customers/:customerId"
              element={
                <RequirePermissionRoute permission="customers:view">
                  <Suspense fallback={<AdminFallback />}>
                    <CustomerDetailPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />

            {/* Production (#9) */}
            <Route path="production">
              <Route
                path=""
                element={
                  <RequirePermissionRoute permission="production:view">
                    <Suspense fallback={<AdminFallback />}>
                      <ProductionPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="new"
                element={
                  <RequirePermissionRoute permission="production:manage">
                    <Suspense fallback={<AdminFallback />}>
                      <ProductionRunFormPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="day-sheet"
                element={
                  <RequirePermissionRoute permission="production:view">
                    <Suspense fallback={<AdminFallback />}>
                      <DaySheetPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path=":runId"
                element={
                  <RequirePermissionRoute permission="production:view">
                    <Suspense fallback={<AdminFallback />}>
                      <ProductionRunDetailPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
            </Route>

            {/* Notifications — reached from the topbar bell, not the sidebar */}
            <Route
              path="notifications"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <NotificationsPage />
                </Suspense>
              }
            />

            {/* Supply chain routes */}
            <Route path="supply-chain">
              <Route
                path="suppliers"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <SuppliersPage />
                  </Suspense>
                }
              />
              <Route
                path="suppliers/:id"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <SupplierDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="purchase-orders"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <PurchaseOrdersPage />
                  </Suspense>
                }
              />
              <Route
                path="purchase-orders/new"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <PurchaseOrderFormPage />
                  </Suspense>
                }
              />
              <Route
                path="purchase-orders/:id"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <PurchaseOrderFormPage />
                  </Suspense>
                }
              />
              <Route
                path="purchase-orders/:id/view"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <PODetailPage />
                  </Suspense>
                }
              />
              <Route
                path="purchase-orders/:id/receive"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <POReceivePage />
                  </Suspense>
                }
              />
              <Route
                path="payables"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <PayablesPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Sales: drafts and the sale screen. Confirmed sales are bills. */}
            <Route
              path="sales"
              element={
                <RequirePermissionRoute permission="orders:view">
                  <Suspense fallback={<AdminFallback />}>
                    <SalesPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="sales/new"
              element={
                <RequirePermissionRoute permission="orders:create">
                  <Suspense fallback={<AdminFallback />}>
                    <SaleEntryPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="sales/:orderId"
              element={
                <RequirePermissionRoute permission="orders:view">
                  <Suspense fallback={<AdminFallback />}>
                    <SaleEntryPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />

            {/* Bills routes */}
            <Route
              path="bills"
              element={
                <RequirePermissionRoute permission="bills:history">
                  <Suspense fallback={<AdminFallback />}>
                    <BillsPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="bills/:id"
              element={
                <RequirePermissionRoute permission="bills:history">
                  <Suspense fallback={<AdminFallback />}>
                    <BillDetailPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="credit-notes"
              element={
                <RequirePermissionRoute permission="bills:history">
                  <Suspense fallback={<AdminFallback />}>
                    <CreditNotesPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />

            {/* Other routes */}
            <Route
              path="customers"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <CustomersPage />
                </Suspense>
              }
            />
            <Route
              path="reports/sales"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <SalesSummaryPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/items"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <ItemSalesPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/menu-performance"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <MenuPerformancePage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="menu/price-bands"
              element={
                <RequirePermissionRoute permission="menu:view">
                  <Suspense fallback={<AdminFallback />}>
                    <PriceBandsPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/pricing-exceptions"
              element={
                <RequirePermissionRoute permission="pricing:view_cost">
                  <Suspense fallback={<AdminFallback />}>
                    <PricingExceptionsPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="quotations"
              element={
                <RequirePermissionRoute permission="quotations:view">
                  <Suspense fallback={<AdminFallback />}>
                    <QuotationsPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="quotations/new"
              element={
                <RequirePermissionRoute permission="quotations:manage">
                  <Suspense fallback={<AdminFallback />}>
                    <QuotationFormPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="quotations/:quotationId/edit"
              element={
                <RequirePermissionRoute permission="quotations:manage">
                  <Suspense fallback={<AdminFallback />}>
                    <QuotationFormPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="quotations/:quotationId"
              element={
                <RequirePermissionRoute permission="quotations:view">
                  <Suspense fallback={<AdminFallback />}>
                    <QuotationDetailPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/business-day"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <BusinessDayPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/margin"
              element={
                <RequirePermissionRoute permission="pricing:view_cost">
                  <Suspense fallback={<AdminFallback />}>
                    <MarginPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/stock-value"
              element={
                <RequirePermissionRoute permission="pricing:view_cost">
                  <Suspense fallback={<AdminFallback />}>
                    <StockValuePage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/cost-check"
              element={
                <RequirePermissionRoute permission="pricing:view_cost">
                  <Suspense fallback={<AdminFallback />}>
                    <CostCheckPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/receivables-ageing"
              element={
                <RequirePermissionRoute permission="receivables:view">
                  <Suspense fallback={<AdminFallback />}>
                    <ReceivablesAgeingPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/sales-by-customer"
              element={
                <RequirePermissionRoute permission="receivables:view">
                  <Suspense fallback={<AdminFallback />}>
                    <SalesByCustomerPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/stock-flow"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <StockFlowPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/gst"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <GSTReportPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/composition"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <CompositionReportPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/payment-reconciliation"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <PaymentReconciliationPage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            <Route
              path="reports/staff-performance"
              element={
                <RequirePermissionRoute permission="reports:view">
                  <Suspense fallback={<AdminFallback />}>
                    <StaffPerformancePage />
                  </Suspense>
                </RequirePermissionRoute>
              }
            />
            {/* Day Close (EOD). Cloud only until the standalone server grows the
                endpoint (#535) — the dashboard ships inside the desktop app, so a
                route present there would 404 against the local server. */}
            <Route
              path="reports/day-close"
              element={
                <RequireAnyPermissionRoute permissions={["sessions:view", "reports:view"]}>
                  <Suspense fallback={<AdminFallback />}>
                    <DayClosePage />
                  </Suspense>
                </RequireAnyPermissionRoute>
              }
            />
            {/* Financial reports are accounting output — same flag as the module */}
            <Route
              path="reports/trial-balance"
              element={
                <RequireFeatureRoute feature={ACCOUNTING_FEATURE}>
                  <RequirePermissionRoute permission="financial_reports:view">
                    <Suspense fallback={<AdminFallback />}>
                      <FinancialReportsPage defaultTab="trial-balance" />
                    </Suspense>
                  </RequirePermissionRoute>
                </RequireFeatureRoute>
              }
            />
            <Route
              path="reports/profit-loss"
              element={
                <RequireFeatureRoute feature={ACCOUNTING_FEATURE}>
                  <RequirePermissionRoute permission="financial_reports:view">
                    <Suspense fallback={<AdminFallback />}>
                      <FinancialReportsPage defaultTab="profit-loss" />
                    </Suspense>
                  </RequirePermissionRoute>
                </RequireFeatureRoute>
              }
            />
            <Route
              path="reports/balance-sheet"
              element={
                <RequireFeatureRoute feature={ACCOUNTING_FEATURE}>
                  <RequirePermissionRoute permission="financial_reports:view">
                    <Suspense fallback={<AdminFallback />}>
                      <FinancialReportsPage defaultTab="balance-sheet" />
                    </Suspense>
                  </RequirePermissionRoute>
                </RequireFeatureRoute>
              }
            />

            {/* Accounting routes — hidden behind the plan flag */}
            <Route
              path="accounting"
              element={
                <RequireFeatureRoute feature={ACCOUNTING_FEATURE}>
                  <LedgerUIBridge>
                    <Outlet />
                  </LedgerUIBridge>
                </RequireFeatureRoute>
              }
            >
              <Route
                path="opening-balances"
                element={
                  <RequirePermissionRoute permission="accounting:view">
                    <Suspense fallback={<AdminFallback />}>
                      <OpeningBalancesPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="accounts"
                element={
                  <RequirePermissionRoute permission="accounting:view">
                    <Suspense fallback={<AdminFallback />}>
                      <AccountsPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="journals"
                element={
                  <RequirePermissionRoute permission="accounting:view">
                    <Suspense fallback={<AdminFallback />}>
                      <JournalsPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="petty-cash"
                element={
                  <RequirePermissionRoute permission="accounting:view">
                    <Suspense fallback={<AdminFallback />}>
                      <PettyCashPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
              <Route
                path="cash-book"
                element={
                  <RequirePermissionRoute permission="accounting:view">
                    <Suspense fallback={<AdminFallback />}>
                      <CashBookPage />
                    </Suspense>
                  </RequirePermissionRoute>
                }
              />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
    </QueryClientProvider>
  );
}
